import api from './baseApi';
import { TemplateProductsHomeListResult } from './typings/templateProducts';
import { AccessMode, AccessRestriction, EntityType, TeamMember, TeamMemberList, UserPermission } from './typings/team';
import { LibraryResponse } from '../typings/libraries';
import { StoreList } from '../typings/user';
import { OptionSetListDto } from './typings/optionSets';
import { ProductListDto } from './typings/product';

export const getTeamMembers = async (from: number, to: number, search?: string): Promise<TeamMemberList> => {
    const response = await api.get(`Team`, { params: { from, to, search: search?.toLocaleLowerCase() } });
    return response.data;
};

export const addTeamMember = async (
    name: string,
    email: string,
    accessMode: AccessMode,
    manageIntegrations: boolean
): Promise<TeamMember> => {
    const response = await api.post(`Team`, { name, email, accessMode, manageIntegrations });
    return response.data;
};

export const updateTeamMember = async (
    id: number,
    accessMode: AccessMode,
    manageIntegrations: boolean
): Promise<void> => {
    await api.put(`Team/${id}`, { accessMode, manageIntegrations });
};

export const removeTeamMember = async (id: number): Promise<void> => {
    await api.delete(`Team/${id}`);
};

export const getTeamMemberPermissions = async (id: number): Promise<UserPermission[]> => {
    const response = await api.get(`Team/${id}/permissions`);
    return response.data;
};

export const getTeamMemberTemplates = async (
    id: number,
    from: number,
    to: number,
    search?: string
): Promise<TemplateProductsHomeListResult> => {
    const response = await api.get(`Team/${id}/templates`, {
        params: { from, to, search: search?.toLocaleLowerCase() }
    });
    return response.data;
};

export const getTeamMemberLibraries = async (
    id: number,
    from: number,
    to: number,
    search?: string
): Promise<LibraryResponse> => {
    const response = await api.get(`Team/${id}/libraries`, {
        params: { from, to, search: search?.toLocaleLowerCase() }
    });
    return response.data;
};

export const getTeamMemberStores = async (
    id: number,
    from: number,
    to: number,
    search?: string
): Promise<StoreList> => {
    const response = await api.get(`Team/${id}/stores`, {
        params: { from, to, search: search?.toLocaleLowerCase() || '' }
    });
    return response.data;
};

export const getTeamMemberProducts = async (
    id: number,
    from: number,
    to: number,
    storeUrl: string,
    search?: string
): Promise<ProductListDto> => {
    const response = await api.get(`Team/${id}/products`, {
        params: { storeUrl, from, to, search: search?.toLocaleLowerCase() }
    });
    return response.data;
};

export const getTeamMemberOptionSets = async (
    id: number,
    from: number,
    to: number,
    search?: string
): Promise<OptionSetListDto> => {
    const response = await api.get(`Team/${id}/sets`, {
        params: { from, to, search: search?.toLocaleLowerCase() }
    });
    return response.data;
};

export const assignTeamMemberEntities = async (id: number, entityIds: string[], entityType: EntityType) => {
    await api.post(`Team/${id}/permissions`, { entityIds, entityType: entityType });
};

export const addTeamMemberEntities = async (id: number, entityIds: string[], entityType: EntityType) => {
    await api.put(`Team/${id}/permissions`, { entityIds, entityType: entityType });
};

export const removeTeamMemberEntities = async (id: number, entityIds: string[], entityType: EntityType) => {
    await api.delete(`Team/${id}/permissions`, { params: { entityIds: entityIds.join(','), entityType: entityType } });
};

export const updateTeamMemberAccessRestriction = async (
    id: number,
    accessRestriction: AccessRestriction,
    entityType: EntityType
): Promise<void> => {
    await api.put(`Team/${id}/access-restriction`, {
        AccessRestriction: accessRestriction,
        AccessEntityType: entityType
    });
};

export const getTeamMemberAccessRestriction = async (memberId: number, entityType: EntityType): Promise<number> => {
    const response = await api.get(`Team/${memberId}/access-restriction?accessEntityType=${entityType}`);
    return response.data.accessRestriction;
};
